import React, { useState, useMemo } from 'react';
import { List, Button, Space, Typography, Popconfirm, Tooltip, Select, Popover, Input, Alert } from 'antd';
import { formatApiError } from '../../../utils/formatApiError';
import { formatUploadDisplayName } from '../../../utils/functions';
import { DeleteOutlined, DownloadOutlined, FolderOpenOutlined } from '@ant-design/icons';
import { FiSearch, FiInfo } from 'react-icons/fi';
// Use react-icons for file types
import { FaImage } from 'react-icons/fa6';
import { FaFilePdf } from 'react-icons/fa';
import { FaFileWord } from 'react-icons/fa';
import { BiSolidFileTxt } from 'react-icons/bi';
import { FaFileCsv } from 'react-icons/fa6';
import { PiFileSqlFill } from 'react-icons/pi';
import { FaFileExcel } from 'react-icons/fa6';

const { Text } = Typography;

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

// Helper to get file type icon (plain, subtle)
const getFileTypeIcon = (file) => {
  const fileType = file.type?.toLowerCase() || '';
  const fileName = file.name?.toLowerCase() || '';
  if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
    return <FaFilePdf style={{ fontSize: 18, color: 'inherit', opacity: 0.7 }} />;
  }
  if (fileType.startsWith('image/') || /\.(jpg|jpeg|png|gif|bmp)$/i.test(fileName)) {
    return <FaImage style={{ fontSize: 18, color: 'inherit', opacity: 0.7 }} />;
  }
  if (fileName.endsWith('.docx') || fileName.endsWith('.doc')) {
    return <FaFileWord style={{ fontSize: 18, color: 'inherit', opacity: 0.7 }} />;
  }
  if (fileName.endsWith('.txt')) {
    return <BiSolidFileTxt style={{ fontSize: 18, color: 'inherit', opacity: 0.7 }} />;
  }
  if (fileName.endsWith('.csv')) {
    return <FaFileCsv style={{ fontSize: 18, color: 'inherit', opacity: 0.7 }} />;
  }
  if (fileName.endsWith('.sql')) {
    return <PiFileSqlFill style={{ fontSize: 18, color: 'inherit', opacity: 0.7 }} />;
  }
  if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) {
    return <FaFileExcel style={{ fontSize: 18, color: 'inherit', opacity: 0.7 }} />;
  }
  if (fileName.endsWith('.xml')) {
    return <BiSolidFileTxt style={{ fontSize: 18, color: 'inherit', opacity: 0.7 }} />;
  }
  // Default icon
  return <BiSolidFileTxt style={{ fontSize: 18, color: 'inherit', opacity: 0.7 }} />;
};

const getListErrorMessage = (error, t) => {
  if (!error) return null;
  if (typeof error === 'string') return error;
  return formatApiError(error.error ?? error, t);
};

const FileList = ({
  files = [],
  loading = false,
  onDelete = () => {},
  onDownload = () => {},
  subDirs = [],
  currentSubDir = '',
  onSubDirChange = () => {},
  subDirLabel = 'Subdirectory',
  t = (s) => s,
  status = 'pending',
  error = null,
  onClearError = () => {},
}) => {
  const [search, setSearch] = useState('');
  const [searchPopoverOpen, setSearchPopoverOpen] = useState(false);

  // Filter files by search
  const filteredFiles = useMemo(() => {
    if (!search) return files;
    const q = search.toLowerCase();
    return files.filter((f) =>
      f.name.toLowerCase().includes(q) || formatUploadDisplayName(f.name).toLowerCase().includes(q)
    );
  }, [files, search]);

  // Info content for popover
  const renderFileInfo = (file) => (
    <div style={{ minWidth: 220 }}>
      <div><b>{t('Name')}:</b> {file.name}</div>
      <div><b>{t('Size')}:</b> {file.sizeFormatted || (file.size ? formatFileSize(file.size) : '-')}</div>
      <div><b>{t('Type')}:</b> {file.type || '-'}</div>
      {file.lastModified && <div><b>{t('Last Modified')}:</b> {new Date(file.lastModified).toLocaleString()}</div>}
      {file.path && <div><b>{t('Path')}:</b> {file.path}</div>}
      {file.fullPath && <div><b>{t('Full Path')}:</b> {file.fullPath}</div>}
      {file.status && <div><b>{t('Status')}:</b> {file.status}</div>}
    </div>
  );

  const listErrorMessage = getListErrorMessage(error, t);

  const searchPopoverContent = (
    <Input
      autoFocus
      allowClear
      size="small"
      placeholder={t('Search files...')}
      value={search}
      onChange={e => setSearch(e.target.value)}
      style={{ width: 180 }}
    />
  );

  return (
    <div>
      {listErrorMessage && (
        <Alert
          message={listErrorMessage}
          type="error"
          showIcon
          closable
          onClose={onClearError}
          style={{ marginBottom: 16 }}
        />
      )}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <Space>
          <FolderOpenOutlined />
          <Text strong>{t(subDirLabel)}:</Text>
          <Select
            value={currentSubDir}
            onChange={onSubDirChange}
            style={{ minWidth: 120 }}
            loading={loading}
          >
            {subDirs.map((dir) => (
              <Select.Option key={dir} value={dir}>{dir}</Select.Option>
            ))}
          </Select>
        </Space>
        <div style={{ marginLeft: 'auto' }}>
          <Popover
            content={searchPopoverContent}
            trigger="click"
            open={searchPopoverOpen}
            onOpenChange={setSearchPopoverOpen}
            placement="bottomRight"
          >
            <Button
              type="text"
              icon={<FiSearch style={{ fontSize: 18 }} />}
              aria-label={t('Search files')}
            />
          </Popover>
        </div>
      </div>
      <List
        loading={loading}
        dataSource={filteredFiles}
        renderItem={file => (
          <List.Item style={{ padding: '12px 12px', flexDirection: 'column', alignItems: 'flex-start', overflow: 'hidden', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
            <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, overflow: 'hidden' }}>
              {getFileTypeIcon(file)}
              <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                <Text ellipsis={{ tooltip: file.name }} style={{ width: '100%', fontWeight: 500 }}>
                  {formatUploadDisplayName(file.name)}
                </Text>
              </div>
            </div>
            <div style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 24, marginTop: 4 }}>
              <Space size="large">
                <Text type="secondary" style={{ fontSize: 12 }}>{file.sizeFormatted || (file.size ? formatFileSize(file.size) : '-')}</Text>
                {file.lastModified && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {new Date(file.lastModified).toLocaleString()}
                  </Text>
                )}
              </Space>
              <Space style={{ marginLeft: 'auto' }}>
                <Tooltip title={t('Download')}>
                  <Button
                    type="text"
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => onDownload(file)}
                  />
                </Tooltip>
                <Popover
                  content={renderFileInfo(file)}
                  trigger="click"
                  placement="right"
                >
                  <Button
                    type="text"
                    icon={<FiInfo style={{ fontSize: 16, verticalAlign: 'middle' }} />}
                    style={{ padding: 0 }}
                    aria-label={t('File info')}
                  />
                </Popover>
                <Popconfirm
                  title={t('Delete this file?')}
                  description={t('This action cannot be undone')}
                  onConfirm={() => onDelete(file)}
                  okText={t('Delete')}
                  cancelText={t('Cancel')}
                  okButtonProps={{ danger: true }}
                >
                  <Tooltip title={t('Delete')}>
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                    />
                  </Tooltip>
                </Popconfirm>
              </Space>
            </div>
          </List.Item>
        )}
        locale={{ emptyText: t('No files found') }}
      />
    </div>
  );
};

export default FileList;
